import { useEffect, useState } from 'react';
import style from './EditItem.module.css';
import FormComp from '../../FormComp';
import styled from 'styled-components';
import { FiDownload, FiTrash2 } from 'react-icons/fi';
import {
    AiOutlineFilePdf,
    AiOutlineFileImage,
    AiOutlineFileWord,
    AiOutlineFileExcel,
    AiOutlineFileUnknown,
} from 'react-icons/ai';
import { ImSpinner8 } from "react-icons/im";
import { getItensByNorma } from '../../../Funcoes/GetItensByNorma';
import { useToast } from '../../../contexts/ToastContext';
import useApi from '../../../hooks/useApi';
import MultiFileUploader from '../../Utils/MultiFileUploader';
import Overlay from '../../Overlay';
import Modal from '../../Utils/Modal';
import { useModal } from '../../../contexts/ModalContext';

const ModalTitle = styled.h3`
    margin: 0 0 18px 0;
    padding: 0 0 12px 2px;
    border-bottom: 1px solid #e5e7eb;
    font-size: 15px;
    font-weight: 700;
    color: #263b4b;
    letter-spacing: 0.02em;
    text-align: left;
    width: 100%;
    box-sizing: border-box;
`;

const ConfirmOverlay = styled.div`
    position: fixed;
    inset: 0;
    z-index: 100000;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(0, 0, 0, 0.45);
`;

const ConfirmBox = styled.div`
    width: min(350px, 92%);
    background-color: #fff;
    border-radius: 8px;
    padding: 20px 16px 16px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const ConfirmTitle = styled.p`
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: #1f2937;
    text-align: center;
    line-height: 1.4;
`;

const ConfirmActions = styled.div`
    display: flex;
    gap: 10px;
    margin-top: 20px;
    width: 100%;
`;

const ConfirmButton = styled.button`
    flex: 1;
    height: 36px;
    border: none;
    border-radius: 8px;
    font-weight: 700;
    font-size: 12px;
    cursor: pointer;
    background-color: ${(p) => (p.$primary ? '#b91c1c' : '#e5e7eb')};
    color: ${(p) => (p.$primary ? '#fff' : '#1f2937')};

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const SecaoAnexos = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: 8px;
`;

const SemAnexos = styled.p`
    margin: 0;
    font-size: 13px;
    color: #6b7280;
    padding: 10px 12px;
    background-color: #f8fafc;
    border: 1px dashed #cbd5e1;
    border-radius: 8px;
    text-align: center;
`;

const CardAnexo = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    background-color: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    min-width: 0;
`;

const AnexoIconWrap = styled.div`
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    background-color: #fff;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
`;

const AnexoInfo = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const AnexoNome = styled.span`
    font-size: 13px;
    font-weight: 500;
    color: #1e293b;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const AnexoAcoes = styled.div`
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 6px;
`;

const BotaoAnexo = styled.button`
    all: unset;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    cursor: pointer;
    transition: background-color 0.15s ease, border-color 0.15s ease;
    color: ${(p) => (p.$danger ? '#b91c1c' : '#1d4ed8')};
    background-color: ${(p) =>
        p.$danger ? 'rgba(185, 28, 28, 0.10)' : 'rgba(29, 78, 216, 0.10)'};
    border: 1px solid transparent;

    &:hover:not(:disabled) {
        background-color: ${(p) =>
            p.$danger ? 'rgba(185, 28, 28, 0.20)' : 'rgba(29, 78, 216, 0.20)'};
        border-color: ${(p) =>
            p.$danger ? 'rgba(185, 28, 28, 0.45)' : 'rgba(29, 78, 216, 0.45)'};
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    &:focus-visible {
        outline: 2px solid ${(p) => (p.$danger ? '#b91c1c' : '#1d4ed8')};
        outline-offset: 2px;
    }
`;

function getFileIcon(filename) {
    const name = String(filename ?? '').toLowerCase();
    if (/\.(png|jpe?g|gif|webp|bmp)$/i.test(name)) {
        return <AiOutlineFileImage size={20} color="#4a90e2" />;
    }
    if (name.endsWith('.pdf')) {
        return <AiOutlineFilePdf size={20} color="#e63946" />;
    }
    if (/\.(doc|docx)$/i.test(name)) {
        return <AiOutlineFileWord size={20} color="#2a6db0" />;
    }
    if (/\.(xls|xlsx)$/i.test(name)) {
        return <AiOutlineFileExcel size={20} color="#207245" />;
    }
    return <AiOutlineFileUnknown size={20} color="#555" />;
}

const normalizeAnexos = (anexos) => {
    if (!Array.isArray(anexos)) return [];
    return anexos
        .map((item) => {
            if (typeof item === 'string') {
                return { id: null, caminho: item };
            }
            return {
                id: item?.id ?? null,
                caminho: item?.caminho ?? '',
                filename: item?.filename ?? '',
            };
        })
        .filter((item) => item.caminho);
};

const EditItem = ({ itemSelected, updateItens, onClose }) => {

    const api = useApi();
    const { closeModal } = useModal();
    const { notifyError, notifySuccess } = useToast();

    const handleClose = () => {
        closeModal();
        if (typeof onClose === 'function') {
            onClose();
        }
    };

    const [loading, setLoading] = useState(true);
    const [disabledForm, setDisabledForm] = useState(false);
    const [itemInfo, setItemInfo] = useState({});

    const [lideres, setLideres] = useState([]);
    const setores = [
        "ADM",
        "ALMOXARIFADO/MATERIAIS",
        "ALTA DIREÇÃO",
        "ANDAIMES",
        "COMERCIAL",
        "CONTABILIDADE",
        "CONTROLE DE OBRAS",
        "ELÉTRICA",
        "ENGENHARIA E ORÇAMENTOS",
        "FABRICAÇÃO (PIPE SHOP)",
        "FINANCEIRO",
        "FISCAL",
        "IÇAMENTO E MOVIMENTAÇÃO DE CARGA",
        "INSTRUMENTAÇÃO",
        "ISOLAMENTO TÉRMICO",
        "JURÍDICO",
        "LOGÍSTICA",
        "MANUTENÇÃO MECÂNICA",
        "MECÂNICA",
        "PINTURA",
        "PLANEJAMENTO/GERENCIAMENTO DE OBRAS",
        "QUALIDADE",
        "RECURSOS HUMANOS",
        "SGI - SISTEMA DE GESTÃO INTEGRADO",
        "SSMA",
        "SUPRIMENTOS",
        "T.I.",
        "TUBULAÇÃO"
    ];
    const [itens, setItens] = useState([]);
    const [selectdFiles, setSelectedFiles] = useState([]);
    const [confirmAnexo, setConfirmAnexo] = useState(null);
    const [deletingAnexo, setDeletingAnexo] = useState(false);

    useEffect(() => {

        if (itemInfo.norma) {

            const dados = getItensByNorma(itemInfo.norma);
            setItens(dados);

        } else {

            setItens([]);

        }

    }, [itemInfo.norma]);

    const initialFetch = async () => {

        setLoading(true);

        try {

            const [item, lideresRes] = await Promise.all([
                api.post('/Painel/API/AuditInterna/GetItemInfo/', {
                    id: itemSelected
                }),
                api.get('/Painel/assets/php/getLideres.php')
            ]);

            console.log(item.data);

            setItemInfo({
                ...item.data,
                anexos: normalizeAnexos(item.data.anexos),
            });
            setLideres(lideresRes.data.map(obj => obj.RA_NOMECMP));

        } catch (e) {

            console.error(e);
            notifyError('Erro ao carregar o item.');

        } finally {

            setLoading(false);

        }

    };

    useEffect(() => {

        if (itemSelected) {
            initialFetch();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemSelected]);

    const getFileName = (path) => path.split('/').pop();

    const anexosList = normalizeAnexos(itemInfo.anexos);

    const openDeleteConfirm = (anexo) => {
        if (!anexo?.id) return;
        setConfirmAnexo(anexo);
    };

    const handleCancelDeleteAnexo = () => {
        if (deletingAnexo) return;
        setConfirmAnexo(null);
    };

    const handleConfirmDeleteAnexo = async () => {
        if (!confirmAnexo?.id || deletingAnexo) return;

        setDeletingAnexo(true);

        try {
            await api.post('/Painel/API/AuditInterna/DeleteAnexo/', {
                id: confirmAnexo.id,
            });

            setItemInfo((prev) => ({
                ...prev,
                anexos: normalizeAnexos(prev.anexos).filter(
                    (anexo) => anexo.id !== confirmAnexo.id
                ),
            }));

            notifySuccess('Anexo removido com sucesso.');
            setConfirmAnexo(null);

            if (typeof updateItens === 'function') {
                await updateItens();
            }
        } catch (error) {
            const msg = error.response?.data?.message ?? 'Erro ao remover anexo.';
            notifyError(msg);
        } finally {
            setDeletingAnexo(false);
        }
    };

    const handleChange = (e) => {

        const { name, value } = e.target;

        if (name === 'norma') {

            setItemInfo({
                ...itemInfo,
                subitem: '',
                [name]: value
            });

        } else if (name === 'conformidade') {

            setItemInfo({
                ...itemInfo,
                fato: '',
                [name]: value
            });

        } else {

            setItemInfo({
                ...itemInfo,
                [name]: value
            });

        }

    };

    const handleSubmit = async (e) => {

        e.preventDefault();
        setDisabledForm(true);

        const formData = new FormData();

        formData.append('id', itemInfo.id);
        formData.append('auditado', itemInfo.auditado);
        formData.append('norma', itemInfo.norma);
        formData.append('item', itemInfo.subitem);
        formData.append('setor', itemInfo.setor);
        formData.append('conformidade', itemInfo.conformidade);
        formData.append('evidencia', itemInfo.evidencia);
        formData.append('fato', itemInfo.fato ?? '');
        formData.append('grupo', itemInfo.grupo);

        if (selectdFiles) {

            selectdFiles.forEach((item) => {

                formData.append('files[]', item.file);

            });

        }

        try {

            await api.post('/Painel/API/AuditInterna/EditItem/', formData);

            await updateItens();
            notifySuccess('Item atualizado com sucesso.');
            handleClose();

        } catch (error) {

            const msg = error.response?.data?.message ?? 'Erro ao salvar o item.';
            notifyError(msg);

        } finally {

            setDisabledForm(false);

        }

    };

    return (
        <Overlay state>
            <Modal header close={handleClose} size="md">
                <ModalTitle>EDITAR ITEM</ModalTitle>
                {
                    loading ?
                        <div className={style.loadingContainer}>
                            <div className={style.loadingBox}>
                                <p className={style.loadingTxt}>CARREGANDO</p>
                                <div className={style.spinnerContainer}>
                                    <ImSpinner8 className={style.spinnerIcon} />
                                </div>
                            </div>
                        </div>
                        :
                        <form
                            className={style.formulario}
                            onSubmit={handleSubmit}
                        >
                            <FormComp
                                title="AUDITADO"
                                type="select"
                                options={lideres}
                                valor={itemInfo.auditado}
                                changeFunc={(e) => handleChange(e)}
                                name="auditado"
                            />
                            <FormComp
                                title="NORMA"
                                type="select"
                                options={[
                                    "ISO 9001:2015",
                                    "ISO 14001:2015",
                                    "ISO 45001:2018"
                                ]}
                                valor={itemInfo.norma}
                                changeFunc={(e) => handleChange(e)}
                                name="norma"
                            />
                            <FormComp
                                title="ITEM"
                                type="select"
                                options={itens}
                                valor={itemInfo.subitem}
                                changeFunc={(e) => handleChange(e)}
                                name="subitem"
                            />
                            <FormComp
                                title="SETOR"
                                type="select"
                                options={setores}
                                valor={itemInfo.setor}
                                changeFunc={(e) => handleChange(e)}
                                name="setor"
                            />
                            <FormComp
                                title="CONFORMIDADE"
                                type="select"
                                options={[
                                    "C",
                                    "NC",
                                    "NA",
                                    "OM"
                                ]}
                                valor={itemInfo.conformidade}
                                changeFunc={(e) => handleChange(e)}
                                name="conformidade"
                            />
                            <FormComp
                                title="EVIDÊNCIA OBJETIVA"
                                type="textarea"
                                valor={itemInfo.evidencia}
                                changeFunc={(e) => handleChange(e)}
                                name="evidencia"
                            />

                            {
                                (itemInfo.conformidade === "NC" || itemInfo.conformidade === "OM") && (
                                    <FormComp
                                        title="FATO"
                                        type="textarea"
                                        valor={itemInfo.fato}
                                        changeFunc={(e) => handleChange(e)}
                                        name="fato"
                                    />
                                )
                            }

                            <SecaoAnexos>
                                <p className={style.anexosTitle}>ANEXOS</p>
                                {anexosList.length >= 1 ? (
                                    anexosList.map((anexo) => {
                                        const nome = anexo.filename;
                                        return (
                                            <CardAnexo key={anexo.id ?? anexo.caminho}>
                                                <AnexoIconWrap>
                                                    {getFileIcon(nome)}
                                                </AnexoIconWrap>
                                                <AnexoInfo>
                                                    <AnexoNome title={nome}>{nome}</AnexoNome>
                                                </AnexoInfo>
                                                <AnexoAcoes>
                                                    <BotaoAnexo
                                                        as="a"
                                                        href={anexo.caminho}
                                                        download
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        title="Baixar anexo"
                                                    >
                                                        <FiDownload size={14} />
                                                    </BotaoAnexo>
                                                    <BotaoAnexo
                                                        type="button"
                                                        $danger
                                                        title="Excluir anexo"
                                                        disabled={!anexo.id || deletingAnexo}
                                                        onClick={() => openDeleteConfirm(anexo)}
                                                    >
                                                        <FiTrash2 size={14} />
                                                    </BotaoAnexo>
                                                </AnexoAcoes>
                                            </CardAnexo>
                                        );
                                    })
                                ) : (
                                    <SemAnexos>Nenhum anexo.</SemAnexos>
                                )}
                            </SecaoAnexos>

                            <p className={style.anexosTitle}>ADICIONAR ANEXOS</p>
                            <MultiFileUploader
                                files={selectdFiles}
                                setFiles={setSelectedFiles}
                                accept="*"
                            />
                            <FormComp
                                type="submit"
                                valor={disabledForm ? 'AGUARDE' : 'SALVAR'}
                                disabled={disabledForm}
                            />
                        </form>
                }
            </Modal>

            {confirmAnexo && (
                <ConfirmOverlay
                    onClick={(e) => {
                        e.stopPropagation();
                        if (e.target === e.currentTarget) {
                            handleCancelDeleteAnexo();
                        }
                    }}
                >
                    <ConfirmBox onClick={(e) => e.stopPropagation()}>
                        <ConfirmTitle>
                            Deseja excluir o arquivo &quot;{getFileName(confirmAnexo.caminho)}&quot;?
                        </ConfirmTitle>
                        <ConfirmActions>
                            <ConfirmButton
                                type="button"
                                onClick={handleCancelDeleteAnexo}
                                disabled={deletingAnexo}
                            >
                                NÃO
                            </ConfirmButton>
                            <ConfirmButton
                                type="button"
                                $primary
                                onClick={handleConfirmDeleteAnexo}
                                disabled={deletingAnexo}
                            >
                                {deletingAnexo ? 'EXCLUINDO…' : 'SIM'}
                            </ConfirmButton>
                        </ConfirmActions>
                    </ConfirmBox>
                </ConfirmOverlay>
            )}
        </Overlay>
    );

};

export default EditItem;
